<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider.js';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  assistant: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const store = useStore();

onMounted(() => {
  store.dispatch('attributes/get');
  store.dispatch('labels/get');
});

const audienceType = ref(props.assistant?.config?.audience_type || 'everyone');
const matchType = ref('all');
const conditions = ref([]);

const { filterTypes } = useContactFilterContext();

// Initialize from assistant data
const initFromAssistant = () => {
  if (props.assistant?.config) {
    audienceType.value = props.assistant.config.audience_type || 'everyone';
    const rawConditions = props.assistant.config.audience_conditions || [];
    
    if (rawConditions.length > 0) {
      matchType.value = rawConditions[0].query_operator === 'or' ? 'any' : 'all';
      conditions.value = rawConditions.map(c => ({
        attributeKey: c.attribute_key || 'email',
        filterOperator: c.filter_operator || 'contains',
        values: Array.isArray(c.values) ? (c.values[0] || '') : (c.values || ''),
        queryOperator: c.query_operator || 'and',
      }));
    } else {
      conditions.value = [];
    }
  }
};

watch(() => props.assistant, initFromAssistant, { immediate: true, deep: true });

const addCondition = () => {
  conditions.value.push({
    attributeKey: 'email',
    filterOperator: 'contains',
    values: '',
    queryOperator: matchType.value === 'all' ? 'and' : 'or',
  });
};

const removeCondition = (index) => {
  conditions.value.splice(index, 1);
};

const handleMatchTypeChange = (newVal) => {
  matchType.value = newVal;
  conditions.value.forEach(c => {
    c.queryOperator = newVal === 'all' ? 'and' : 'or';
  });
};

const handleSubmit = () => {
  const formattedConditions = conditions.value.map(c => ({
    attribute_key: c.attributeKey,
    filter_operator: c.filterOperator,
    query_operator: matchType.value === 'all' ? 'and' : 'or',
    values: Array.isArray(c.values) ? c.values : [c.values],
  }));

  emit('submit', {
    config: {
      audience_type: audienceType.value,
      audience_conditions: audienceType.value === 'specific' ? formattedConditions : [],
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-[48rem]">
    <div class="flex flex-col gap-2">
      <h6 class="text-n-slate-12 text-base font-medium">
        {{ t('CAPTAIN.ASSISTANTS.SETTINGS.AUDIENCE.TITLE', 'Audience') }}
      </h6>
      <span class="text-n-slate-11 text-sm">
        {{ t('CAPTAIN.ASSISTANTS.SETTINGS.AUDIENCE.DESCRIPTION', 'Choose whether this assistant responds to everyone or only to a specific audience.') }}
      </span>
    </div>

    <!-- Everyone Card -->
    <div
      class="flex gap-4 p-5 rounded-xl border cursor-pointer transition-all"
      :class="audienceType === 'everyone' ? 'border-n-blue-9 bg-n-blue-1/10 ring-1 ring-n-blue-9' : 'border-n-weak bg-n-surface-2'"
      @click="audienceType = 'everyone'"
    >
      <input
        type="radio"
        v-model="audienceType"
        value="everyone"
        class="mt-1"
      />
      <div class="flex flex-col gap-1">
        <span class="text-sm font-semibold text-n-slate-12">Everyone</span>
        <span class="text-xs text-n-slate-11">Captain responds to every conversation in connected inboxes.</span>
      </div>
    </div>

    <!-- Specific Audience Card -->
    <div
      class="flex flex-col gap-5 p-5 rounded-xl border transition-all"
      :class="audienceType === 'specific' ? 'border-n-blue-9 bg-n-blue-1/10 ring-1 ring-n-blue-9' : 'border-n-weak bg-n-surface-2'"
    >
      <div class="flex gap-4 cursor-pointer" @click="audienceType = 'specific'">
        <input
          type="radio"
          v-model="audienceType"
          value="specific"
          class="mt-1"
        />
        <div class="flex flex-col gap-1">
          <span class="text-sm font-semibold text-n-slate-12">Specific audience</span>
          <span class="text-xs text-n-slate-11">Captain responds only to customers who match the conditions below.</span>
        </div>
      </div>

      <!-- Specific Audience Conditions Builder -->
      <div v-if="audienceType === 'specific'" class="mt-2 flex flex-col gap-4 border-t border-n-weak pt-4 pl-8">
        <div class="flex items-center gap-2 text-sm text-n-slate-12">
          <span>Match</span>
          <select
            :value="matchType"
            @change="handleMatchTypeChange($event.target.value)"
            class="rounded-lg border-n-weak text-sm bg-n-background text-n-slate-12 py-1 px-3 focus:ring-n-blue-11"
          >
            <option value="all">all</option>
            <option value="any">any</option>
          </select>
          <span>of the following</span>
        </div>

        <div class="flex flex-col gap-3">
          <ul class="space-y-3">
            <ConditionRow
              v-for="(condition, index) in conditions"
              :key="index"
              v-model:attribute-key="condition.attributeKey"
              v-model:values="condition.values"
              v-model:filter-operator="condition.filterOperator"
              v-model:query-operator="condition.queryOperator"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeCondition(index)"
            />
          </ul>

          <div class="flex items-center gap-4 mt-2">
            <Button
              :label="t('CAPTAIN.ASSISTANTS.SETTINGS.AUDIENCE.ADD_CONDITION', '+ Add condition')"
              variant="link"
              color="blue"
              size="sm"
              class="!p-0"
              @click="addCondition"
            />
          </div>
        </div>
      </div>
    </div>

    <Button
      :label="t('CAPTAIN.ASSISTANTS.SETTINGS.AUDIENCE.UPDATE', 'Update')"
      color="blue"
      class="w-fit mt-2"
      @click="handleSubmit"
    />
  </div>
</template>
